import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { CheckCircleIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";

// Context
import AssessmentContext from "../../../context/assessment/AssessmentContext";
import SnackbarContext from "../../../context/assessment/SnackbarContext";
import WebSocketContext from "../../../context/WebSocketContext";

// Controller
import AssessmentSessionParticipant from "../../../controller/assessment/assessment_participant/session";

// Helper
import logRender from "../../../helper/assessment/logRender";
import errorHandler from "../../../helper/assessment/errorHandler";

// Component
import Header from "../../../components/assessment/assessment_participant/Header";
import PdfViewer from "../../../components/assessment/assessment_participant/PdfViewer";
import IntrayViewer from "../../../components/assessment/assessment_participant/IntrayViewer";
import FormUpload from "../../../components/assessment/assessment_participant/FormUpload";
import {
  ConsentStartPanel,
  FullscreenReturnBar,
  IntegrityWarning,
  ProtectedTestContent,
} from "../../../components/shared/sessionIntegrity/SessionIntegrity";
import useSessionIntegrity, {
  IntegrityStateEvent,
} from "../../../components/shared/sessionIntegrity/useSessionIntegrity";

function Session() {
  // Context
  const { assessment, participant, timeRemaining, setTimeRemaining } = useContext(AssessmentContext);
  const { setNotif } = useContext(SnackbarContext);
  const { socket } = useContext(WebSocketContext)
  // Ref
  const webcamRef = useRef<Webcam>(null);
  const pendingAwaySnapshotsRef = useRef<Map<string, string>>(new Map());
  const markSnapshotFailureRef = useRef<
    (sourceClientEventId: string, occurredAt: string, reason: string) => void
  >(() => undefined);
  // State
  const [isCheckActiveSession, setIsCheckActiveSession] = useState<boolean>(false);
  const [activeSession, setActiveSession] = useState<TPActiveSession>();
  const [participantSession, setParticipantSession] = useState<TPParticipantSession>();
  const [showFormUpload, setShowFormUpload] = useState<boolean>(false);
  const [isCheckCamera, setIsCheckCamera] = useState<boolean>(false);
  const [cameraAllowed, setCameraAllowed] = useState<boolean>(false);
  const [captureImage, setCaptureImage] = useState<string>();
  // screencap every x second = default 5 minutes
  const [intervalGetImage, setIntervalGetImage] = useState<number>(60);
  // interval check timer x second = default 105 second
  const intervalCheckTimer = 105;
  const activeSessionId = activeSession?.id;

  useEffect(() => {
    logRender({ type: "page", name: "assessment/Session" });
  }, []);

  const getActiveSession = useCallback(async () => {
    try {
      // Get active session
      const aspc = new AssessmentSessionParticipant();
      const resActiveSession = await aspc.getActiveAssessment(assessment.assessment_id.toString(), participant.id.toString());
      setActiveSession(resActiveSession.data.row);
      if (resActiveSession.data.row?.setting?.snap_interval_assessment) {
        const interval = parseInt(resActiveSession.data.row?.setting?.snap_interval_assessment);
        if (!isNaN(interval)) {
          setIntervalGetImage(interval)
        }
      }
    } catch (error) {
      // no notification here we avoid conflict message from websocket
      // const errorMessage = errorHandler(error);
      // setNotif({ type: "error", message: errorMessage });
      // reset when assessment closed
      setActiveSession(undefined);
    }
    setIsCheckActiveSession(true);
  }, [assessment.assessment_id, participant.id]);

  // get open session
  useEffect(() => {
    getActiveSession();
  }, [getActiveSession]);

  // WebSocket is only a refresh signal. Polling keeps open/close reliable when it disconnects.
  useEffect(() => {
    const polling = window.setInterval(getActiveSession, 12000);
    return () => window.clearInterval(polling);
  }, [getActiveSession]);

  useEffect(() => {
    if (!socket) {
      return;
    }
    socket.onmessage = (event) => {
      const wsdata = JSON.parse(JSON.parse(event.data)?.body ?? "");
      const scope = wsdata?.scope;
      const target = wsdata?.target;
      const action = wsdata?.action;
      const message = wsdata?.message;
      if (scope === "assessment" && target === "participant") {
        if (action === "check open session") {
          getActiveSession();
        }
        if (message) {
          setNotif({ type: "info", message: message });
        }
      }
    };
    return () => {
      socket.onmessage = null;
    };
  }, [socket, getActiveSession, setNotif]);

  const getParticipantSession = useCallback(async () => {
    try {
      // Get Participant Session Progress (time and file answer)
      const aspc = new AssessmentSessionParticipant();
      const resParticipantSession = await aspc.getParticipantAssessment(
        participant.id.toString(),
        activeSession?.id.toString() ?? ""
      );
      setParticipantSession(resParticipantSession.data.row);
    } catch (error) {
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  }, [activeSession?.id, setNotif, participant.id]);

  const sessionIsRunning =
    Boolean(participantSession?.is_open) &&
    participantSession?.start_time !== null &&
    (participantSession?.timing_mode === "admin_controlled" ||
      (timeRemaining !== null && timeRemaining > 0));
  const captureTabAwaySnapshot = useCallback((event: IntegrityStateEvent) => {
    if (event.phase !== "away") return;
    const image = webcamRef.current?.getScreenshot();
    if (!image) {
      markSnapshotFailureRef.current(
        event.clientEventId,
        event.occurredAt,
        "camera_unavailable"
      );
      return;
    }
    pendingAwaySnapshotsRef.current.set(event.clientEventId, image);
  }, []);
  const uploadTabAwaySnapshot = useCallback(
    async (event: { client_event_id: string; occurred_at: string }) => {
      const image = pendingAwaySnapshotsRef.current.get(event.client_event_id);
      if (!image) {
        markSnapshotFailureRef.current(
          event.client_event_id,
          event.occurred_at,
          "snapshot_not_cached"
        );
        return;
      }
      if (!participantSession?.id || !participantSession.assessment_id) return;
      pendingAwaySnapshotsRef.current.delete(event.client_event_id);
      try {
        const controller = new AssessmentSessionParticipant();
        await controller.saveWebcam(
          participantSession.id.toString(),
          participantSession.assessment_id.toString(),
          {
            base64_image: image,
            client_event_id: event.client_event_id,
            capture_reason: "tab_away",
          },
          participant.access_key
        );
      } catch {
        markSnapshotFailureRef.current(
          event.client_event_id,
          event.occurred_at,
          "upload_failed"
        );
      }
    },
    [participant.access_key, participantSession?.assessment_id, participantSession?.id]
  );
  const integrity = useSessionIntegrity({
    product: "assessment",
    sessionId: participantSession?.id,
    sessionKind: activeSession
      ? `category-${activeSession.category_id}`
      : undefined,
    accessKey: participant.access_key,
    running: sessionIsRunning,
    onReturnSync: getParticipantSession,
    onStateEvent: captureTabAwaySnapshot,
    onTabAwayEventPersisted: (event) => {
      void uploadTabAwaySnapshot(event);
    },
  });
  markSnapshotFailureRef.current = (sourceClientEventId, occurredAt, reason) =>
    integrity.recordSnapshotFailure(sourceClientEventId, occurredAt, reason);

  useEffect(() => {
    if (activeSessionId !== undefined) {
      setParticipantSession(undefined);
      getParticipantSession();
    } else {
      setParticipantSession(undefined);
      setTimeRemaining(null);
    }
  }, [activeSessionId, getParticipantSession, setTimeRemaining]);

  // to make sure remaining time is correct
  // set remaining time context
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      if (Math.floor(timeRemaining % intervalCheckTimer) === 0) {
        console.log(new Date() + " check timer");
        getParticipantSession();
      }
    }
  }, [timeRemaining, getParticipantSession]);

  // set remaining time context
  useEffect(() => {
    if (participantSession?.remaining_time !== undefined && participantSession?.remaining_time !== null) {
      setTimeRemaining(participantSession.remaining_time);
    } else if (participantSession?.timing_mode === "admin_controlled") {
      setTimeRemaining(null);
    }
  }, [participantSession?.remaining_time, participantSession?.timing_mode, setTimeRemaining]);

  const startAssessment = async () => {
    try {
      // Get Participant Session Progress (time and file answer)
      const aspc = new AssessmentSessionParticipant();
      await aspc.startAssessment(participantSession?.id.toString() ?? "", {
        time_limit: activeSession?.time_limit ?? 0,
      }, participant.access_key);
      getParticipantSession();
    } catch (error) {
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  };

  const getCamera = useCallback(async () => {
    const sessionIsRunning =
      participantSession?.is_open &&
      participantSession.start_time !== null &&
      (participantSession.timing_mode === "admin_controlled" ||
        (timeRemaining !== null && timeRemaining > 0));
    if (sessionIsRunning) {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        setCameraAllowed(true);
      } catch (error) {
        setCameraAllowed(false);
      }
      setIsCheckCamera(true);
      // console.log(timeRemaining, intervalGetImage)
      if (
        timeRemaining !== null &&
        Math.floor(timeRemaining % intervalGetImage) === 0
      ) {
        if (webcamRef !== null && webcamRef.current !== null) {
          const image = webcamRef.current.getScreenshot();
          if (image !== null) {
            console.log(new Date() + " capt image");
            setCaptureImage(image);
          }
        }
      }
    }
  }, [timeRemaining, intervalGetImage, participantSession]);

  // check camera is open when remaining time set
  useEffect(() => {
    getCamera();
  }, [getCamera]);

  // upload capture image
  useEffect(() => {
    const uploadCapturedImage = async () => {
      if (captureImage !== undefined) {
        try {
          // Get Participant Session Progress (time and file answer)
          const aspc = new AssessmentSessionParticipant();
          await aspc.saveWebcam(participantSession?.id.toString() ?? "", participantSession?.assessment_id.toString() ?? "", {
            base64_image: captureImage,
          }, participant.access_key);
        } catch (error) {
          const errorMessage = errorHandler(error);
          setNotif({ type: "error", message: errorMessage });
        }
      }
    };
    uploadCapturedImage();
  }, [captureImage, setNotif, participantSession?.id, participantSession?.assessment_id, participant.access_key]);

  return (
    <div className="print:hidden w-screen h-screen flex flex-col overflow-hidden">
      <FormUpload
        showFormUpload={showFormUpload}
        setShowFormUpload={setShowFormUpload}
        participantSession={participantSession}
        getParticipantSession={getParticipantSession}
      />
      <IntegrityWarning message={integrity.warning} />
      <FullscreenReturnBar integrity={integrity} />
      <Header activeSession={activeSession} />
      {/* check active session */}
      {isCheckActiveSession === false ? (
        <div className="h-full flex flex-col justify-center items-center">
          <div className="font-light text-lg">Cek Sesi Aktif</div>
        </div>
      ) : (
        <>
          {/* If no session active (open) */}
          {activeSession === undefined ? (
            <div className="h-full flex flex-col justify-center items-center">
              <div className="font-semibold text-xl">Belum ada sesi dibuka</div>
              <div className="font-light text-lg">Mohon tunggu dan ikuti arahan assessor</div>
            </div>
          ) : (
            <>
              {/* if session active (open) */}
              {/* check if user not start the session (start_time === null)*/}
              {participantSession !== undefined && participantSession.start_time === null ? (
                <div className="h-full flex flex-col justify-center items-center">
                  <ConsentStartPanel
                    integrity={integrity}
                    title={activeSession?.description ?? "Assessment"}
                    detail={
                      activeSession.timing_mode === "admin_controlled" ? (
                        <span>Sesi berjalan sampai assessor menutupnya.</span>
                      ) : (
                        <span>
                          Waktu assessment:{" "}
                          <b>{activeSession?.time_limit} menit</b>
                        </span>
                      )
                    }
                    onStart={startAssessment}
                  />
                </div>
              ) : (
                <ProtectedTestContent
                  integrity={integrity}
                  participantName={participant.full_name}
                  participantId={participant.participant_id}
                >
                <div className="h-full bg-white">
                  {/* here the user has been start the assessment session */}
                  {/* check if times up */}
                  {(participantSession?.timing_mode === "admin_controlled" ||
                    (timeRemaining !== null && timeRemaining > 0)) &&
                    participantSession?.is_open && (
                    <>
                      <Webcam
                        height={200}
                        width={200}
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        className="-z-10 absolute"
                        videoConstraints={{ facingMode: "user" }}
                      />
                      {/* loading for checking camera */}
                      {isCheckCamera ? (
                        <>
                          {/* Check if camera on */}
                          {cameraAllowed ? (
                            <div className="h-screen">
                              {activeSession.category.includes("In-Tray Simulation") ? (
                                <IntrayViewer participantSession={participantSession} activeSession={activeSession} />
                              ) : (
                                <PdfViewer pdfUrl={activeSession?.file_path ?? ""} />
                              )}
                            </div>
                          ) : (
                            <div className="h-screen flex flex-col justify-center items-center">
                              <div className="font-semibold text-xl">Akses kamera tidak di izinkan</div>
                              <div className="font-light text-lg text-center">
                                Akses kamera diperlukan untuk melanjutkan proses assessment, mohon berikan izin akses
                                kamera kepada halaman ini.
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="h-screen flex flex-col justify-center items-center">
                          <div className="font-light text-lg text-center">Check Kamera</div>
                        </div>
                      )}
                    </>
                  )}
                  {participantSession?.timing_mode === "timed" &&
                    timeRemaining !== null &&
                    timeRemaining <= 0 && (
                    <div className="h-screen flex flex-col justify-center items-center">
                      <div className="font-semibold text-xl">Waktu Anda sudah habis</div>
                      <div className="font-light text-lg">Mohon tunggu arahan assessor.</div>
                      {activeSession.category.includes("In-Tray Simulation") === false && (
                        <>
                          {/* if file answer has been uploaded yet (null) */}
                          {participantSession !== undefined && participantSession.file_path !== null ? (
                            <div className="font-semibold text-lg mt-2 text-green-700">
                              File jawaban <b>telah</b> diupload
                            </div>
                          ) : (
                            <div className="font-semibold text-lg mt-2 text-red-500">
                              File jawaban <b>belum</b> diupload
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {/* if file answer not uploaded yet (null) */}
                  {activeSession.category.includes("In-Tray Simulation") === false && (
                    <>
                      {participantSession?.file_path === null ? (
                        <button
                          onClick={() => setShowFormUpload(true)}
                          className="flex fixed justify-center items-center w-full md:w-auto bottom-0 md:bottom-6 md:right-10 md:rounded-full border border-red-500 px-4 py-1 text-white md:text-red-500 bg-red-500 md:bg-transparent hover:bg-red-500 hover:text-white"
                        >
                          <ArrowUpTrayIcon className="w-5 mr-2" /> Unggah jawaban
                        </button>
                      ) : (
                        <div className="flex fixed justify-center items-center w-full md:w-auto bottom-0 md:bottom-6 md:right-10 md:rounded-full border border-green-500 px-4 py-1 text-white bg-green-500">
                          <CheckCircleIcon className="w-5 mr-2" /> Jawaban sudah diunggah
                        </div>
                      )}
                    </>
                  )}
                </div>
                </ProtectedTestContent>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Session;
